import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

const SEARCH_QUERY = 'q';
const TARGET_GENDER_PARAM = 'filter.v.t.shopify.target-gender';
const AUDIENCE_PARAM = 'audience';

const TARGET_GENDER_TO_AUDIENCE = {
  'gid://shopify/Metaobject/459097538887': 'dames',
  'gid://shopify/Metaobject/541331063111': 'heren',
  'gid://shopify/Metaobject/541331226951': 'jongens',
  'gid://shopify/Metaobject/541331259719': 'meisjes',
};

const AUDIENCE_TO_TARGET_GENDER = {
  dames: 'gid://shopify/Metaobject/459097538887',
  heren: 'gid://shopify/Metaobject/541331063111',
  jongens: 'gid://shopify/Metaobject/541331226951',
  meisjes: 'gid://shopify/Metaobject/541331259719',
};

function normalizeAudienceParameters(parameters, currentParameters = new URLSearchParams()) {
  const selectedTargetGender =
    parameters.getAll(TARGET_GENDER_PARAM).at(-1) ||
    currentParameters.get(TARGET_GENDER_PARAM) ||
    AUDIENCE_TO_TARGET_GENDER[(currentParameters.get(AUDIENCE_PARAM) || '').toLowerCase()] ||
    '';

  const audience = TARGET_GENDER_TO_AUDIENCE[selectedTargetGender] || '';

  parameters.delete(TARGET_GENDER_PARAM);
  parameters.delete(AUDIENCE_PARAM);

  if (selectedTargetGender && audience) {
    parameters.set(TARGET_GENDER_PARAM, selectedTargetGender);
    parameters.set(AUDIENCE_PARAM, audience);
  }

  return parameters;
}

class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];
  #renderPromise = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #onBreakpointChange = (event) => {
    if (!event.matches) return;

    const drawer = document.getElementById('filters-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  };

  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    const parameters = new URLSearchParams(formData);
    const currentURL = new URL(window.location.href);
    const currentParameters = currentURL.searchParams;

    if (parameters.get('filter.v.price.gte') === '') parameters.delete('filter.v.price.gte');
    if (parameters.get('filter.v.price.lte') === '') parameters.delete('filter.v.price.lte');

    parameters.delete('page');

    const searchQuery = currentParameters.get(SEARCH_QUERY);
    if (searchQuery) parameters.set(SEARCH_QUERY, searchQuery);

    normalizeAudienceParameters(parameters, currentParameters);
    return parameters;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  #buildURL(parameters) {
    const url = new URL(window.location.href);
    url.search = '';

    for (const [key, value] of parameters.entries()) {
      url.searchParams.append(key, value);
    }

    return url;
  }

  #updateURL() {
    const currentURL = new URL(window.location.href);
    const parameters = this.createURLParameters();
    const nextURL = this.#buildURL(parameters);

    if (nextURL.href === currentURL.href) return null;

    history.pushState({ urlParameters: parameters.toString() }, '', nextURL.toString());
    return nextURL;
  }

  updateFilters = () => {
    const nextURL = this.#updateURL();
    if (!nextURL) return;

    if (this.#renderPromise) return;

    const render = () => sectionRenderer.renderSection(this.sectionId);
    this.#renderPromise = this.closest('dialog') ? render() : startViewTransition(render, ['product-grid']);

    const promise = this.#renderPromise;
    this.#renderPromise.finally(() => {
      if (this.#renderPromise === promise) this.#renderPromise = null;
    });

    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(promise);
    } else {
      this.#dispatchCollectionUpdateEvent(promise);
    }
  };

  updateFiltersByURL(url) {
    const currentURL = new URL(window.location.href);
    const nextURL = new URL(url, window.location.origin);

    normalizeAudienceParameters(nextURL.searchParams, currentURL.searchParams);

    if (nextURL.href === currentURL.href) return;

    history.pushState('', '', nextURL.toString());
    this.updateFilters();
  }

  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

  #dispatchSearchUpdateEvent(renderPromise) {
    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: new URL(window.location.href).searchParams.get(SEARCH_QUERY) || '',
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey(),
        },
        promise: deferredPromise.promise,
      }),
    );

    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }

  #dispatchCollectionUpdateEvent(renderPromise) {
    const rawId = this.dataset.collectionId;
    const productFilters = CollectionUpdateEvent.parseProductFilters();
    const deferredPromise = CollectionUpdateEvent.createPromise();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: {
          id: rawId && /^\d+$/.test(rawId) ? rawId : null,
          handle: this.dataset.collectionHandle,
          productsCount: this.#getResultsCount(),
        },
        ...(productFilters && { productFilters }),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferredPromise.promise,
      }),
    );

    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

class FacetInputsComponent extends Component {
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const input = event.target.querySelector('input');
    if (!(input instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      input.checked = !input.checked;
      this.updateFilters();
    }
  }

  // Prefetch is intentionally disabled on mobile to avoid duplicate network work.
  prefetchPageImmediate(event) {
    if (!mediaQueryLarge.matches) return;
    if (!(event.target instanceof HTMLElement)) return;

    const input = event.target.querySelector('input');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    const form = this.closest('form');
    const facetsForm = this.closest('facets-form-component');
    if (!form || !(facetsForm instanceof FacetsFormComponent)) return;

    const formData = new FormData(form);
    if (!input.checked) formData.append(input.name, input.value);

    const parameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);
    for (const [key, value] of parameters.entries()) url.searchParams.append(key, value);

    if (input.checked) url.searchParams.delete(input.name, input.value);
    sectionRenderer.getSectionHTML(this.closest('.shopify-section')?.id || '', true, url);
  }

  prefetchPage() {}
  cancelPrefetchPage() {}

  #updateSelectedFacetSummary() {
    const inputs = this.refs.facetInputs;
    if (!inputs) return;

    const checkedInputs = inputs.filter((input) => input.checked);
    const details = this.closest('details');
    const status = details?.querySelector('facet-status-component');

    if (status instanceof FacetStatusComponent) status.updateListSummary(checkedInputs);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency || 'EUR';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat || '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  #onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey) return;

    const validKeys = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(validKeys)) event.preventDefault();
  };

  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;
    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSummary();
  }

  #adjustToValidValues(input) {
    if (!input || input.value.trim() === '') return;

    const value = convertMoneyToMinorUnits(input.value, this.currency) || 0;
    const min = convertMoneyToMinorUnits(input.getAttribute('data-min') || '0', this.currency) || 0;
    const max = convertMoneyToMinorUnits(input.getAttribute('data-max') || '0', this.currency) || 0;

    if (value < min) input.value = formatMoney(min, this.moneyFormat, this.currency);
    if (value > max) input.value = formatMoney(max, this.moneyFormat, this.currency);
  }

  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const status = details?.querySelector('facet-status-component');

    if (status instanceof FacetStatusComponent) status.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

class FacetClearComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.checked = false;
      input.value = '';
    });

    const facetsForm = this.closest('facets-form-component');
    if (facetsForm instanceof FacetsFormComponent) facetsForm.updateFilters();
  }

  #handleKeyUp = (event) => {
    if (!event.metaKey && event.key === 'Enter') this.clearFilter(event);
  };

  #handleFilterUpdate = (event) => {
    const eventSection = event.target?.closest('.shopify-section');
    const ownSection = this.closest('.shopify-section');
    if (eventSection && ownSection && eventSection !== ownSection) return;

    const filters = event instanceof SearchUpdateEvent ? event.search?.productFilters || [] : event.productFilters || [];
    if (this.refs.clearButton instanceof Element) {
      this.refs.clearButton.classList.toggle('facets__clear--active', filters.length > 0);
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');
    if (facetsForm instanceof FacetsFormComponent) facetsForm.updateFiltersByURL(url);
  }

  #handleFilterUpdate = (event) => {
    const eventSection = event.target?.closest('.shopify-section');
    const ownSection = this.closest('.shopify-section');
    if (eventSection && ownSection && eventSection !== ownSection) return;

    const filters = event instanceof SearchUpdateEvent ? event.search?.productFilters || [] : event.productFilters || [];
    const activeClass = this.getAttribute('active-class') || 'active';

    if (this.refs.clearButton instanceof Element) {
      this.refs.clearButton.classList.toggle(activeClass, filters.length > 0);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

class SortingFilterComponent extends Component {
  handleKeyDown(event) {
    const options = Array.from(this.querySelectorAll('[role="option"]'));
    const current = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let index = current ? options.indexOf(current) : 0;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      index = Math.min(index + 1, options.length - 1);
      this.#moveFocus(options, index);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      index = Math.max(index - 1, 0);
      this.#moveFocus(options, index);
    } else if (event.key === 'Enter' || event.key === ' ') {
      const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
      if (option) {
        event.preventDefault();
        option.querySelector('input[type="radio"]')?.click();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeDropdown();
    }
  }

  handleToggle = () => {
    const details = this.refs.details;
    const summary = this.refs.summary;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;
    summary.setAttribute('aria-expanded', String(details.open));
  };

  updateFilterAndSorting(event) {
    const facetsForm = this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    if (this.refs.details instanceof HTMLDetailsElement) this.refs.details.open = false;
  }

  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const details = this.querySelector('details');
    const status = details?.querySelector('facet-status-component');
    if (!(status instanceof FacetStatusComponent)) return;
    status.textContent = event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName || '' : '';
  }

  #moveFocus(options, index) {
    options.forEach((option) => {
      if (option instanceof HTMLElement) option.tabIndex = -1;
    });

    if (options[index] instanceof HTMLElement) {
      options[index].tabIndex = 0;
      options[index].focus();
    }
  }

  #closeDropdown() {
    if (this.refs.details instanceof HTMLDetailsElement) this.refs.details.open = false;
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

class FacetStatusComponent extends Component {
  updateListSummary(inputs) {
    const count = inputs.length;
    if (this.getAttribute('facet-type') === 'swatches') {
      this.#updateSwatchSummary(inputs, count);
    } else {
      this.#updateBubbleSummary(inputs, count);
    }
  }

  #updateSwatchSummary(inputs, count) {
    const status = this.refs.facetStatus;
    if (!(status instanceof HTMLElement)) return;

    status.classList.remove('facets__bubble');

    if (count === 0) {
      status.innerHTML = '';
      return;
    }

    if (count > 3) {
      status.textContent = String(count);
      status.classList.add('facets__bubble');
      return;
    }

    status.innerHTML = inputs
      .map((input) => {
        const swatch = input.parentElement?.querySelector('span.swatch');
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = input.getAttribute('aria-label') || '';
        return (swatch?.outerHTML || '') + label.outerHTML;
      })
      .join('');
  }

  #updateBubbleSummary(inputs, count) {
    const status = this.refs.facetStatus;
    if (!(status instanceof HTMLElement)) return;

    status.classList.remove('facets__bubble');

    if (count === 0) {
      status.innerHTML = '';
     return; 
    }

    if (this.dataset.filterStyle === 'horizontal' && count === 1) {
      status.textContent = inputs[0]?.dataset.label || '';
      return;
    }

    status.textContent = String(count);
    status.classList.add('facets__bubble');
  }

  updatePriceSummary(minInput, maxInput) {
    const status = this.refs.facetStatus;
    if (!(status instanceof HTMLElement)) return;

    if (!minInput.value && !maxInput.value) {
      status.innerHTML = '';
      return;
    }

    const currency = status.dataset.currency || 'EUR';
    const min = convertMoneyToMinorUnits(minInput.value || '0', currency) || 0;
    const max = convertMoneyToMinorUnits(maxInput.value || status.dataset.rangeMax || '0', currency) || 0;
    status.innerHTML = `${this.#formatMoney(min, currency)}–${this.#formatMoney(max, currency)}`;
  }

  clearSummary() {
    if (this.refs.facetStatus instanceof HTMLElement) this.refs.facetStatus.innerHTML = '';
  }

  #formatMoney(value, currency) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';
    return formatMoney(value, this.refs.moneyFormat.content.textContent || '{{amount}}', currency);
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}